<script setup lang="ts">
    import KButton from "@/components/Button.vue";
    import { generateSchema } from "@/components/forms/Form/schemaHelpers";
    import KFilter from "@/components/KFilter/KFilter.vue";
    import { useStore } from "@/store";
    // For Pycharm users
    // noinspection ES6UnusedImports
    import moment from "moment/moment";
    import { computed, onMounted, ref } from "vue";

    const store = useStore();

    const props = defineProps<{
        /**
         * Startvärde i formuläret
         */
        defaultValue?: {
            [key: string]: any;
        };
        /**
         * Ska filtrering emittas vid mount?
         */
        filterOnMount?: boolean;
        keepOnClear?: string[];
        tableStateName?: string;
    }>();

    const filterRef = ref();

    const personId = computed(() => store.getters["patientData/personId"]);
    const currentProfile = computed(() => store.getters["userData/currentProfile"]);

    defineExpose({
        setFieldValue: (props: {
            name: string; // Namn på fältet
            value: unknown; // Nya värdet
        }) => filterRef?.value?.setFieldValue?.(props),
        manualSubmit: () => filterRef?.value?.manualSubmit?.(),
    });

    const emit = defineEmits<{
        (event: "submit", payload: { value: any }): void;
    }>();

    const schema = generateSchema({
        addToAll: {
            ignoreEmptyData: true,
        },
        schema: [
            {
                component: "KDateTimepicker",
                name: "preliminart_datum__gte",
                label: "Önskatdatum från",
            },
            {
                component: "KDateTimepicker",
                name: "preliminart_datum__lte",
                label: "Önskatdatum till",
            },
            {
                component: "KText",
                name: "patient__personId",
                label: "PersonID",
            },
            {
                component: "KText",
                name: "patient__namn",
                label: "Patientnamn",
            },
            {
                component: "KDateTimepicker",
                name: "documentTime__date__gte",
                label: "Skapat datum från",
            },
            {
                component: "KDateTimepicker",
                name: "documentTime__date__lte",
                label: "Skapat datum till",
            },
            {
                component: "KDateTimepicker",
                name: "bokad_operation_datum__gte",
                label: "Bokat datum fr.o.m",
            },
            {
                component: "KDateTimepicker",
                name: "bokad_operation_datum__lte",
                label: "Bokat datum t.o.m",
            },
            {
                component: "KNumber",
                name: "opnr",
                label: "Operationsnummer",
            },
            {
                component: "ProfileAutocomplete",
                name: "current_bokad_huvudoperator",
                label: "Operatör",
                allowSearch: true,
                urlParams: {
                    roll__code: "kirurg",
                },
                autoPosition: true,
                multiple: true,
            },
            {
                component: "ProfileAutocomplete",
                name: "ass_operatorer",
                label: "Assisterande operatör",
                allowSearch: true,
                urlParams: {
                    operator: true,
                },
                autoPosition: true,
                multiple: true,
            },
            {
                component: "JaNejAutocomplete",
                name: "huvudoperator_ej_bestamd",
                label: "Operatör ej bestämd",
                autoPosition: true,
                toBoolean: true,
            },
            {
                component: "OperationStatusAutocomplete",
                multiple: true,
                autoPosition: true,
                useFilter: false,
            },
            {
                component: "JaNejAutocomplete",
                name: "preliminarbokning",
                label: "Preliminärbokad",
                autoPosition: true,
                toBoolean: true,
            },
            {
                component: "PreanestesibedomningAutocomplete",
                name: "preanestesibedomning",
                label: "Preanestesibedömning",
                multiple: true,
                autoPosition: true,
            },
            {
                component: "KontaktorsakAutocomplete",
                name: "kontaktorsak",
                label: "Kontaktorsak",
                autoPosition: true,
                multiple: true,
            },
            {
                component: "TypoperationAutocomplete",
                name: "typoperation",
                label: "Typoperation",
                autoPosition: true,
                retrieveOptionsOnMount: true,
                multiple: true,
            },
            {
                component: "HuvudoperationskodAutocomplete",
                name: "huvudoperationskod",
                label: "Huvudoperationskod",
                autoPosition: true,
                required: false,
            },
            {
                component: "HuvuddiagnoskodAutocomplete",
                autoPosition: true,
                required: false,
            },
            {
                component: "PrioritetAutocomplete",
                autoPosition: true,
                multiple: true,
            },
            {
                component: "JaNejAutocomplete",
                name: "kort_varsel",
                label: "Kort varsel",
                autoPosition: true,
                toBoolean: true,
            },
            {
                component: "KNumber",
                name: "optid",
                label: "Optid(min)",
            },
            {
                component: "VardformAutocomplete",
                label: "Vårdform",
                autoPosition: true,
                multiple: true,
            },
            {
                component: "AvtalAutocomplete",
                autoPosition: true,
                multiple: true,
            },
            {
                component: "OrgunitAutocomplete",
                name: "operationsenhet",
                label: "Operationsenhet",
                autoPosition: true,
                urlParams: { vardenhetstyp: "operationsenhet" },
            },
            {
                component: "AnestesiAutocomplete",
                label: "Anestesiform",
                name: "anestesi",
                autoPosition: true,
                multiple: true,
            },
            {
                component: "ProfileAutocomplete",
                name: "anestesiolog",
                label: "Anestesiolog",
                allowSearch: true,
                urlParams: {
                    roll__code: "anestesilakare",
                },
                autoPosition: true,
            },
            {
                component: "StudieKvalitetsregisterAutocomplete",
                name: "studie_kvalitetsregister",
                label: "Studie/Kvalitetsregister",
                multiple: true,
                autoPosition: true,
            },
            {
                component: "KDateTimepicker",
                name: "datum_rontgenbilder_till_op",
                label: "Datum för röntgenbilder till op",
            },
        ],
    });

    const onFilter = ({ formData }: { formData: any }) => {
        emit("submit", { value: formData });
    };

    onMounted(async () => {
        if (props.filterOnMount) {
            emit("submit", { value: await filterRef?.value?.manualSubmit?.() });
        }
    });
</script>

<template>
    <KFilter
        ref="filterRef"
        name="operationskoordinering"
        :schema="schema"
        :defaultValue="defaultValue"
        :keepOnClear="keepOnClear"
        :tableStateName="tableStateName"
        @submit="onFilter"
    >
        <template #quick-actions="{ callback }">
            <KButton
                v-if="personId"
                type="button"
                variant="selectable"
                size="adaptable"
                @click="
                    callback({
                        values: {
                            patient__personId: personId,
                        },
                        keep: ['operationsenhet'],
                    })
                "
            >
                Aktiv patient
            </KButton>
            <KButton
                type="button"
                variant="selectable"
                size="adaptable"
                @click="
                    callback({
                        values: {
                            documentTime__date__gte: moment().subtract(1, 'day').format('yyyy-MM-DD'),
                            documentTime__date__lte: moment().subtract(1, 'day').format('yyyy-MM-DD'),
                        },
                        keep: ['operationsenhet'],
                    })
                "
            >
                Skapad igår
            </KButton>
            <KButton
                type="button"
                variant="selectable"
                size="adaptable"
                @click="
                    callback({
                        values: {
                            status: 'Att hantera',
                        },
                        keep: ['operationsenhet'],
                    })
                "
            >
                Att hantera
            </KButton>
            <KButton
                type="button"
                variant="selectable"
                size="adaptable"
                @click="
                    callback({
                        values: {
                            status: 'Klar för operationsbokning',
                        },
                        keep: ['operationsenhet'],
                    })
                "
            >
                Klar för operationsbokning
            </KButton>
            <KButton
                type="button"
                variant="selectable"
                size="adaptable"
                @click="
                    callback({
                        values: {
                            preanestesibedomning: ['Att göra', 'Komplettering utförd'],
                        },
                        keep: ['operationsenhet'],
                    })
                "
            >
                Preanestesibedömning att hantera
            </KButton>
        </template>
    </KFilter>
</template>

<style scoped lang="scss">
    .operationskoordinering-filter {
    }
</style>
